<template>
  <div class="wrapper">
    <textarea
      v-bind="$attrs"
      ref="markdown-editor"
    />
  </div>
</template>

<script>
import EasyMDE from 'easymde'
import DOMPurify from 'dompurify'
import { escapeHtml } from '@/helpers'
import { marked } from 'marked'

export default {
  name: 'VMarkdownEditor',
  props: {
    value: {
      type: String,
      required: false,
      default: () => '',
    },
    profile: {
      type: String,
      required: false,
      default: () => 'full',
    },
  },

  data () {
    const customTokenizer = new marked.Tokenizer()

    // disable the following tokens in inline mode
    if (this.profile === 'inline') {
      customTokenizer.code = (src) => null
      customTokenizer.fences = (src) => null
      customTokenizer.heading = (src) => null
      customTokenizer.hr = (src) => null
      customTokenizer.blockquote = (src) => null
      customTokenizer.list = (src) => null
      customTokenizer.def = (src) => null
      customTokenizer.table = (src) => null
      customTokenizer.lheading = (src) => null
      customTokenizer.text = (src) => null
    }

    return {
      editor: null,
      tokenizer: customTokenizer,
    }
  },

  mounted () {
    let toolbar = ['bold', 'italic', 'strikethrough', '|', 'heading', 'unordered-list', 'ordered-list', '|', 'link', 'table', '|', 'preview', 'side-by-side']
    if (this.profile === 'inline') {
      toolbar = ['bold', 'italic', 'strikethrough', 'link', '|', 'preview']
    }

    const mdeConfig = {
      element: this.$refs['markdown-editor'],
      spellChecker: false,
      sideBySideFullscreen: false,
      forceSync: true,
      toolbarButtonClassPrefix: 'mde-tool', // this is important since some classes conflict with the app styles
      toolbar,
      renderingConfig: {
        markedOptions: {
          gfm: false,
          walkTokens: this.processToken,
          tokenizer: this.tokenizer,
        },
        singleLineBreaks: false,
        sanitizerFunction: (renderedHTML) => {
          return DOMPurify.sanitize(renderedHTML)
        },
      },
    }

    this.editor = new EasyMDE(mdeConfig)

    // listen to input changes
    this.editor.codemirror.on('change', () => {
      this.$emit('input', this.editor.value())
    })

    if (this.profile === 'inline') {
      this.editor.codemirror.setOption('mode', 'text/plain')
    }

    // set the value in the next tick (after the editor is initialized by EasyMDE)
    this.$nextTick(() => {
      this.editor.value(this.value)

    })
  },

  beforeDestroy () {
    // Remove the editor before the component is destroyed
    this.editor.toTextArea()
    this.editor = null
  },

  methods: {
    processToken (token) {
      // convert images to simple links
      if (token.type === 'image') {
        token.type = 'link'
        token.text = '!' + token.text
        token.tokens = [{ type: 'text', raw: token.text, text: token.text }]
      }

      // disable html rendering
      if (token.type === 'html') {
        token.type = 'text'
        token.text = escapeHtml(token.text)
        token.raw = escapeHtml(token.raw)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  @import "easymde/dist/easymde.min.css";
</style>
